import React, {
  useState,
  useEffect,
  FC,
  ReactNode,
  KeyboardEvent,
} from 'react'
import useOutsideClick from '../../Core/Hooks/useOutsideClick/useOutsideClick'
import { useTranslation } from 'react-i18next'

import Spinner from '../../Core/Loading/Spinner/Spinner'
import Icon from '../../Core/Iconography/Icon/Icon'
import Font from '../../Core/Typography/Font/Font'
import Label from '../Label/Label'
import Checkbox from '../../Atoms/Checkbox/Checkbox'
import RadioButton from '../../Atoms/RadioButton/RadioButton'

import {
  SelectWrapper,
  SelectMain,
  SelectOptionsWrapper,
  SelectOptions,
  SelectOption,
  Placeholder,
  ErrorLabel,
  InputWrapper,
  FilterInputWrapper,
  FilterInput,
  FilterInputCloseWrapper,
  EmptyWrapper,
  GroupRadioButtonWrapper,
  GroupCheckboxWrapper,
  GroupOpenIconWrapper
} from './Dropdown.styles'
import { DropdownProps, SelectProps, OptionGroupProps } from './types'

const OptionGroup: FC<OptionGroupProps> = props => {
  const { parentDisabled, childsDisabled, index, label, treeSelected, nestedOptions, selectedList, multiple, firstLevelMultiple, checkboxClick, setTreeSelected } = props
  const [openGroup, setOpenGroup] = useState(false)

  const groupSelected: boolean = treeSelected?.toString() === [index, null].toString()
  const id = `combo1-${index}`

  return (
    <>
      <SelectOption
        justifyContent='space-between'
        role='option'
        tabIndex={0}
        id={id}
        key={id}
        data-testid={id}
        label={label || ''}
        aria-selected={groupSelected ? 'true' : 'false'}
      >
        <GroupRadioButtonWrapper
          onClick={() => {
            if (!parentDisabled && setTreeSelected) setTreeSelected([index, null])
          }}
        >
          {
            firstLevelMultiple ? (
              <Checkbox
                id={id}
                small
                readOnly
                label={label || ''}
                checked={selectedList?.filter(item => item[0] === index && item[1] === null).length > 0}
                onClick={() => {
                  const indexOfSelectedItem = (list: number[][]) => list?.findIndex(item => item[0] === index && item[1] === null)
                  checkboxClick({index, disabledOption: parentDisabled, multipleOption: firstLevelMultiple, findIndexFunction: indexOfSelectedItem })
                }}
              />
            ) : (
              <RadioButton
                readOnly
                ellipsis
                removeMargins
                size='s'
                disabled={parentDisabled}
                key={id}
                name={id}
                label={label || ''}
                checked={groupSelected}
              />
            )
          }

        </GroupRadioButtonWrapper>
        <GroupOpenIconWrapper onClick={() => setOpenGroup(bool => !bool)}>
          <Icon name={openGroup ? 'minus' : 'plus'} size='m' color='dark' />
        </GroupOpenIconWrapper>
      </SelectOption>
      {openGroup && nestedOptions.map((obj, nestedIndex) => {
        const selected = !multiple
          ? treeSelected?.toString() === [index, nestedIndex].toString()
          : selectedList?.filter(item => item[0] === index && item[1] === nestedIndex).length > 0
        const nestedId = `${id}-${nestedIndex}`

        const indexOfNestedItem = (list: number[][]) => list?.findIndex(item => item[0] === index && item[1] === nestedIndex)

        return (
          <SelectOption
            tab
            justifyContent='space-between'
            role='option'
            tabIndex={0}
            key={nestedId}
            id={nestedId}
            data-testid={nestedId}
            label={obj?.label}
            aria-selected={selected ? 'true' : 'false'}
          >
            <GroupCheckboxWrapper>
              { multiple ? (
                <Checkbox
                  id={nestedId}
                  small
                  readOnly
                  removeMargins
                  label={obj?.label || ''}
                  checked={selected}
                  disabled={childsDisabled}
                  onClick={() => {
                    checkboxClick({index, disabledOption: childsDisabled, multipleOption: multiple, findIndexFunction: indexOfNestedItem, nestedIndex })
                  }}
                />
              ) : (
                <RadioButton
                  readOnly
                  ellipsis
                  removeMargins
                  size='s'
                  disabled={childsDisabled}
                  key={nestedId}
                  name={nestedId}
                  label={obj?.label || ''}
                  checked={selected}
                  onChange={() => {
                    checkboxClick({index, disabledOption: childsDisabled, multipleOption: multiple, findIndexFunction: indexOfNestedItem, nestedIndex })
                  }}
                />
              )}
            </GroupCheckboxWrapper>
          </SelectOption>
        )})}
    </>
  )
}

OptionGroup.defaultProps = {
  parentDisabled: false,
  childsDisabled: false,
}

export const Select: FC<SelectProps> = props => {
  const {
    required,
    disabled,
    multiple,
    darkMode,
    toUpperCase,
    firstLevelMultiple,
    search,
    setSearchValue,
    name,
    placeholder,
    pattern,
    outsideIndexSelected,
    outsideSelectedList,
    outsideTreeSelected,
    options,
    onChange,
    noReset,
    bgTransparent,
    dontClearValue,
    readOnly,
    placeholderAsValue
  } = props

  // eslint-disable-next-line
  const { t } = useTranslation<any>()
  const [ref, isOpen, setOpen] = useOutsideClick(false)
  const [minWidth, setMinWidth] = useState(null)
  const [filterValue, setFilterValue] = useState('')
  const [indexSelected, setIndexSelected] = useState(outsideIndexSelected)
  const [selectedList, setSelectedList] = useState(outsideSelectedList)
  const [treeSelected, setTreeSelected] = useState(outsideTreeSelected)
  const [onFocus, setFocus] = useState(null)
  const [onFocusOption, setFocusOption] = useState(null)

  useEffect(() => {
    setMinWidth(ref?.current?.offsetWidth)
  }, [])

  useEffect(() => {
    setIndexSelected(outsideIndexSelected)
  }, [outsideIndexSelected])

  useEffect(() => {
    setSelectedList(outsideSelectedList)
  }, [outsideSelectedList])

  useEffect(() => {
    setTreeSelected(outsideTreeSelected)
  }, [outsideTreeSelected])

  useEffect(() => {
    if (props.inputUpperCase) setFilterValue(value => (value?.toUpperCase()))
  }, [filterValue])

  useEffect(() => {
    if (disabled && !noReset) {
      setIndexSelected(null)
      setSelectedList([])
      setTreeSelected([null, null])
    }
  }, [disabled])

  useEffect(() => {
    if (onChange && options?.[0]?.options?.length > 0) onChange({
      required,
      name,
      treeSelected,
      options
    })
  }, [treeSelected])

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    //  On press enter key + on focus (Accessibility)
    if (event?.keyCode === 13 && onFocus) setOpen(!isOpen)
  }

  const handleKeyDownOption = (
    event: KeyboardEvent<HTMLElement>,
    index: number
  ) => {
    //  On press enter key + on focus (Accessibility)
    if (event?.keyCode === 13 && index === onFocusOption) {
      setIndexSelected(index)
      if (onChange) onChange({
        required,
        name,
        value: `${index}`,
        index,
        options
      })
    }
  }

  const onChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value
    if (!pattern || value?.match(pattern)) setFilterValue(value)
    if (setSearchValue) setSearchValue(value)
  }

  const resetFilter = () => setFilterValue('')

  const checkboxClick: OptionGroupProps['checkboxClick'] = ({
    index,
    disabledOption,
    multipleOption,
    findIndexFunction,
    nestedIndex,
    noNestedOptions,
    onChangePayload
  }) => {
    const nullOrNestedIndex = nestedIndex === undefined ? null : nestedIndex

    if(!disabledOption && setTreeSelected && !multipleOption && !noNestedOptions) setTreeSelected([index, nullOrNestedIndex])
    if(noNestedOptions && setIndexSelected && !multiple) setIndexSelected(index)

    const modifyList = (list: number[][]) => {
      if (findIndexFunction(list) > -1) {
        const listCopy = [...list]
        listCopy.splice(findIndexFunction(list), 1)
        return listCopy
      }

      return [...list, noNestedOptions ? index : [index, nullOrNestedIndex]]
    }

    // @ts-ignore
    if(multipleOption) setSelectedList(modifyList)

    const multipleAndOnChange = multiple && onChange
    const onChangeCallCondition = nullOrNestedIndex === null ? onChange : multipleAndOnChange

    const payload = onChangePayload || {
      name: `${index}${nullOrNestedIndex === null ? '' : '-'}${nullOrNestedIndex}`,
      value: [index, nullOrNestedIndex].toString(),
    }

    if(onChangeCallCondition) onChange(payload)
  }

  const Options: ReactNode =
    !options || !Array.isArray(options) || options?.length === 0
      ? null
      : options.map((obj, index) => obj?.options?.length > 0 ? (
        <OptionGroup
          key={index}
          index={index}
          label={obj?.label || ''}
          parentDisabled={obj?.parentDisabled}
          childsDisabled={obj?.childsDisabled}
          nestedOptions={obj?.options}
          treeSelected={treeSelected}
          setTreeSelected={setTreeSelected}

          multiple={multiple}
          firstLevelMultiple={firstLevelMultiple}
          selectedList={selectedList}
          checkboxClick={checkboxClick}
        />
      ) : (
        <SelectOption
          justifyContent='flex-start'
          role='option'
          tabIndex={0}
          id={`combo1-${index}`}
          key={`combo1-${index}`}
          data-testid={`combo1-${index}`}
          label={obj?.label}
          multiple={multiple}
          aria-selected={index === indexSelected ? 'true' : 'false'}
          // @ts-ignore
          selected={multiple ? selectedList.includes(index) : index === indexSelected}
          onFocus={() => setFocusOption(index)}
          onBlur={() => setFocusOption(null)}
          onKeyDown={(event: KeyboardEvent<HTMLElement>) =>
            handleKeyDownOption(event, index)
          }
          onClick={() => {
            // @ts-ignore
            const indexOfSelectedItem = (list: number[]) => list?.findIndex(item => item === index)
            checkboxClick({
              index,
              multipleOption: multiple,
              // @ts-ignore
              findIndexFunction: indexOfSelectedItem,
              noNestedOptions: true,
              onChangePayload: {required, name, value: `${index}`, index, options }
            })
            !multiple && setOpen(false)
          }}
        >
          {multiple ? (
            <Checkbox
              small
              readOnly
              label={obj?.label || ''}
              // @ts-ignore
              checked={selectedList.includes(index)}
            />
          ) : (
            <Font ellipsis tag='h6' toUpperCase={toUpperCase}>
              {obj?.label || ''}
            </Font>
          )}
        </SelectOption>
      )).filter(element => search ? true : (filterValue === '' || element?.props?.label?.toLowerCase()?.includes(filterValue?.toLowerCase())))

  const id = `combobox-${name}`
  const idOptions = `listbox-dropdowns-${name}`

  const showPlaceholder = typeof placeholder === 'string' && placeholder !== '' && (
    (typeof indexSelected !== 'number' && !Array.isArray(options?.[0]?.options))
        || (multiple || Array.isArray(options?.[0]?.options) && treeSelected?.[0] === null && treeSelected?.[1] === null)
  )
      && selectedList.length === 0

  const getSelectedLabel = () => {
    if (treeSelected?.toString() !== [null, null].toString()) {
      if (treeSelected?.[1] === null) {
        return options?.[treeSelected?.[0] as number]?.label || ''
      }
      return options?.[treeSelected?.[0] as number]?.options?.[treeSelected?.[1] as number]?.label || ''
    }

    return (
      (multiple && !firstLevelMultiple)
      // @ts-ignore
        ? selectedList.map(item => options[`${item}`]?.label).join(', ')
        : options?.[indexSelected as number]?.label || ''
    )
  }
  return (
    <SelectWrapper
      ref={ref}
      fixWidth={props.fixWidth}
      minWidth={!props.utilizeMinWidth && minWidth}
      utilizeMinWidth={props.utilizeMinWidth}
      selectMargin={props.selectMargin}
    >
      <SelectMain
        justifyContent='space-between'
        aria-controls='listbox1'
        aria-haspopup='listbox'
        aria-label={id}
        aria-labelledby={id}
        id={id}
        role='combobox'
        tabIndex={0}
        aria-expanded={`${isOpen}`}
        isOpen={isOpen}
        isError={props.isError}
        disabled={disabled}
        darkMode={darkMode}
        removeBorder={props.removeBorder}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onKeyDown={handleKeyDown}
        onClick={() => !disabled && setOpen(!isOpen)}
        readOnly={readOnly}
      >
        {(!dontClearValue && disabled) ? (
          <Placeholder ellipsis/>
        ) : showPlaceholder ? (
          <Placeholder placeholderAsValue={placeholderAsValue} ellipsis>{placeholder}</Placeholder>
        ) : (
          <Font
            ellipsis
            tag='h6'
            data-testid='combo1-selected-label'
            color={darkMode ? 'blank' : 'dark'}
            toUpperCase={toUpperCase}
          >
            {getSelectedLabel()}
          </Font>
        )}
        <Icon name='chevron-down' size='s' color={darkMode ? 'blank' : 'ultraViolet'} />
      </SelectMain>
      <SelectOptionsWrapper
        data-testid='selectOptionsWrapper'
        isOpen={isOpen}
        extraSelectionWidth={props.extraSelectionWidth}
      >
        {(search || props.filter) && (
          <FilterInputWrapper>
            <FilterInput
              data-testid='input'
              value={filterValue}
              placeholder={props.filterPlaceholder}
              onChange={onChangeFilter}
            />
            <FilterInputCloseWrapper data-testid='resetFilter' onClick={resetFilter}>
              <Icon name='close' size='s' color='dark' />
            </FilterInputCloseWrapper>
          </FilterInputWrapper>
        )}
        {props.isLoading ? <Spinner removeFullscreen data-testid='dropdownSpinner' /> : (
          props.empty ? (
            <EmptyWrapper data-testid='emptyWrapper'>
              <Font>{t('global.empty')}</Font>
            </EmptyWrapper>
          ) : (
            <SelectOptions
              data-testid='listbox'
              role='listbox'
              id={idOptions}
              aria-label={idOptions}
              aria-labelledby={idOptions}
              tabIndex={-1}
              bgTransparent={bgTransparent}
            >
              {Options}
            </SelectOptions>
          ))}
      </SelectOptionsWrapper>
    </SelectWrapper>
  )
}

const Dropdown: FC<DropdownProps> = ({
  bgTransparent,
  darkMode,
  disabled,
  dontClearValue,
  empty,
  error,
  extraSelectionWidth,
  filter,
  filterPlaceholder,
  firstLevelMultiple,
  fixWidth,
  gridArea,
  inputUpperCase,
  isForm,
  isLoading,
  label,
  marginInputWrapper,
  multiple,
  name,
  noReset,
  pattern,
  placeholder,
  placeholderAsValue,
  onChange,
  options,
  outsideIndexSelected,
  outsideSelectedList,
  outsideTreeSelected,
  readOnly,
  removeBorder,
  removeEllipsis,
  required,
  search,
  selectMargin,
  setSearchValue,
  tooltipLabel,
  toUpperCase,
  utilizeMinWidth,
  value,
  width,
  wrapperWidth
}) => {
  // eslint-disable-next-line
  const { t } = useTranslation<any>()

  const isError: boolean = error !== ''
  const isDisabled: boolean = disabled || (search ? false : options?.length === 0)

  const getSelectedIndex = (): number | null => {
    if (typeof outsideIndexSelected === 'number') return outsideIndexSelected
    if (value !== '') {
      const output = Number(value)
      return isNaN(output) ? null : output
    }
    return null
  }
  const selectedIndex: number | null = getSelectedIndex()

  return (
    <InputWrapper
      isForm={isForm}
      gridArea={gridArea}
      marginInputWrapper={marginInputWrapper}
      readOnly={readOnly}
      wrapperWidth={wrapperWidth}
    >
      <Label
        label={label}
        name={name}
        required={required}
        tooltipLabel={tooltipLabel}
        removeEllipsis={removeEllipsis}
      />
      <Select
        name={name}
        isLoading={isLoading}
        isError={isError}
        disabled={isDisabled}
        required={required}
        darkMode={darkMode}
        removeBorder={removeBorder}
        toUpperCase={toUpperCase}
        fixWidth={fixWidth}
        width={width}
        utilizeMinWidth={utilizeMinWidth}
        multiple={multiple}
        firstLevelMultiple={firstLevelMultiple}
        search={search}
        empty={empty}
        inputUpperCase={inputUpperCase}
        extraSelectionWidth={extraSelectionWidth}
        setSearchValue={setSearchValue}
        filter={filter}
        placeholder={placeholder}
        // @ts-ignore:next-line
        filterPlaceholder={filterPlaceholder || t('components.dropdown.filterPlaceholder')}
        pattern={pattern}
        outsideIndexSelected={selectedIndex}
        outsideSelectedList={outsideSelectedList}
        outsideTreeSelected={outsideTreeSelected}
        options={options}
        onChange={onChange}
        noReset={noReset}
        bgTransparent={bgTransparent}
        dontClearValue={dontClearValue}
        selectMargin={selectMargin}
        readOnly={readOnly}
        placeholderAsValue={placeholderAsValue}
      />
      {isError && <ErrorLabel>{error}</ErrorLabel>}
    </InputWrapper>
  )
}

Dropdown.defaultProps = {
  isLoading: false,
  required: false,
  disabled: false,
  removeBorder: false,
  fixWidth: false,
  multiple: false,
  search: false,
  empty: false,
  filter: false,
  isForm: false,
  extraSelectionWidth: false,
  name: 'untitled',
  label: '',
  placeholder: '',
  filterPlaceholder: '',
  error: '',
  outsideIndexSelected: null,
  outsideTreeSelected: [null, null],
  outsideSelectedList: [],
  options: [],
}

export default Dropdown
